import _ from "lodash";
import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { MDBIcon, MDBCardBody } from "mdbreact";

import useModal from "hooks/useModal";
import { useAuth } from "hooks/useAuth";
import { fetchSiteProperties } from "actions/sites/sitePropertyActions";
import { fetchCustomProperties } from "actions/properties/customPropertyActions";

import Table from "components/common/tables/Table";
import Modal from "components/common/Modal";
import SectionHeaderMenu from "components/nav/SectionHeaderMenu";
import EmptyList from "components/common/EmptyList";
import SitePropertyCreate from "./SitePropertyCreate";
import SitePropertyUpdate from "./SitePropertyUpdate";
import SitePropertyDelete from "./SitePropertyDelete";

const SitePropertyList = () => {
  // hooks
  const {
    modalOpen,
    headerText,
    setHeaderText,
    formId,
    footer,
    setFooter,
    actions,
    setActions,
    selected,
    setSelected,
    params,
    setParams,
    setModalState
  } = useModal();
  const dispatch = useDispatch();
  const auth = useAuth();

  // redux hooks
  const {
    propertiesAvailable: sitePropertiesAvailable,
    lastAction
  } = useSelector(state => state.siteProperties);

  const { propertiesAvailable } = useSelector(state => state.customProperties);

  // local state
  const [tableData, setTableData] = useState({
    columns: [
      {
        label: "#",
        field: "id",
        sort: "desc"
      },
      {
        label: "Name",
        field: "property.name",
        sort: "desc",
        minimal: "lg"
      },
      {
        label: "Type",
        field: "property.property_type.name",
        sort: "desc"
      },
      {
        label: "Required",
        field: "required",
        sort: "desc"
      },
      {
        label: "",
        field: "buttons",
        sort: "disabled",
        minimal: "lg"
      }
    ]
  });

  const toggleModal = useCallback(
    (e, type, item) => {
      if (e) e.preventDefault();

      if (type === "new") {
        setHeaderText("New Site Property");
        setFooter(true);
        setActions([
          {
            text: "Submit",
            color: "primary",
            additionalProps: {
              form: formId,
              type: "submit"
            }
          }
        ]);
      } else if (type === "edit") {
        setHeaderText("Edit Site Property");
        setFooter(true);
        setActions([
          {
            text: "Update",
            color: "primary",
            additionalProps: {
              form: formId,
              type: "submit"
            }
          }
        ]);
        setSelected(item);
      } else if (type === "delete") {
        setHeaderText("Delete Site Property");
        setFooter(true);
        setActions([
          {
            text: "Delete",
            color: "danger",
            additionalProps: {
              form: formId,
              type: "submit"
            }
          }
        ]);
        setSelected(item);
      }
      if (type) {
        setModalState(true);
      } else {
        setModalState(false);
      }
    },
    [formId, setActions, setFooter, setHeaderText, setModalState, setSelected]
  );

  // use effect setups
  useEffect(() => {
    dispatch(fetchSiteProperties());
    dispatch(fetchCustomProperties());
  }, [dispatch]);

  const sectionHeaderMenu = {
    order: 2,
    show: true,
    linkItems: [
      {
        name: (
          <>
            <MDBIcon icon="fas fa-plus" /> Add Site Property
          </>
        ),
        url: `/sites/properties`,
        exact: true,
        additionalProps: {
          active: true,
          onClick: e => toggleModal(e, "new"),
          className: "btn-sm"
        }
      }
    ]
  };

  const renderModalBody = () => {
    if (headerText === "New Site Property") {
      return (
        <SitePropertyCreate
          formId={formId}
          params={params}
          setParams={setParams}
          closeModal={() => setModalState(false)}
        />
      );
    } else if (headerText === "Edit Site Property") {
      return (
        <SitePropertyUpdate
          formId={formId}
          params={params}
          setParams={setParams}
          selected={selected}
          closeModal={() => setModalState(false)}
        />
      );
    } else if (headerText === "Delete Site Property") {
      return (
        <SitePropertyDelete
          formId={formId}
          selected={selected}
          closeModal={() => setModalState(false)}
        />
      );
    }

    return "";
  };

  const renderTable = () => {
    if (_.size(sitePropertiesAvailable)) {
      return (
        <Table
          section="Property"
          tableData={tableData}
          setTableData={setTableData}
          rowData={sitePropertiesAvailable}
          sort={["id", "desc"]}
          pageLengthOpts={[50, 100, 150]}
          toggleModal={toggleModal}
          updatedState={lastAction}
          downloadableCSV
          editButton={auth?.user?.is_admin}
          deleteButton={auth?.user?.is_admin}
        />
      );
    } else if (_.isEmpty(propertiesAvailable)) {
      return (
        <EmptyList
          name="custom property"
          namePlural="custom properties"
          customText="At least one custom property is required before making site properties. "
          userRole={auth?.user?.user_level}
          createElement={<Link to="/admin/properties">Create</Link>}
        />
      );
    } else if (_.isEmpty(sitePropertiesAvailable)) {
      return (
        <EmptyList
          name="site property"
          namePlural="site properties"
          userRole={auth?.user?.user_level}
          createElement={
            <span className="link" onClick={e => toggleModal(e, "new")}>
              Create
            </span>
          }
        />
      );
    }

    return "";
  };

  return (
    <>
      <SectionHeaderMenu
        menuData={_.isEmpty(propertiesAvailable) || sectionHeaderMenu}
      />
      <MDBCardBody>{renderTable()}</MDBCardBody>
      <Modal
        isOpen={modalOpen}
        toggle={toggleModal}
        headerText={headerText}
        includeFooter={footer}
        actionButtons={actions}
      >
        {renderModalBody()}
      </Modal>
    </>
  );
};

export default SitePropertyList;
